import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Row, Col } from 'antd'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../../state_manager/hooks/useAppDispatch'

import { getAccessToken } from '../../../../core/auth/authService'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { DetailPanel } from './parts/DetailPanel.tsx'
import { DealOverviewSection } from './sections/DealOverviewSection.tsx'
import { InteractionsSection } from '../../../interactions/components/InteractionsSection.tsx'
import { TasksSection } from '../../../tasks/components/TasksSection.tsx'
import {
  createInteraction,
  updateInteraction,
  deleteInteraction,
  fetchInteractionsForDeal,
  delinkInteraction
} from '../../../interactions/services/interactionsService.ts'
import { deleteTask as deleteTaskThunk } from '../../../../store/slices/tasksSlice'
import { createTask, updateTask } from '../../../tasks/services/tasksService.ts'
import { fetchDealById } from '../../services/dealsService.ts'
import { UNKNOWN_CONTACT } from '../../../../state_manager/constants/strings.ts'
import { DealCommitmentEntryFields } from '../../../../state_manager/data_models/deal_commitment_entry.ts'
import { DealFields } from '../../../../state_manager/data_models/deal_record.ts'
import { InteractionRecordFields } from '../../../../state_manager/data_models/interaction_record.ts'
import { TaskRecordFields } from '../../../../state_manager/data_models/task_record.ts'
import { UserEntryFields } from '../../../../state_manager/data_models/user_entry.ts'
import { store } from '../../../../state_manager/store.ts'
import {
  setActiveRecord,
  updateActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import { updateDealRecord } from '../../../../state_manager/state_update_actions/deals/deal_actions.ts'
import { selectDealById } from '../../../../state_manager/state_update_actions/deals/selectors.ts'
import {
  addInteraction,
  updateInteraction as updateInteractionAction,
  setInteractions,
  removeInteraction
} from '../../../../state_manager/state_update_actions/interactions/interaction_actions.ts'
import type { DealContacts } from './parts/DealTeam.tsx'

const MemoizedTasksSection = React.memo(function MemoizedTasksSection(props: any) {
  return <TasksSection {...props} />
})

const MemoizedInteractionsSection = React.memo(function MemoizedInteractionsSection(props: any) {
  return <InteractionsSection {...props} />
})

const MemoizedDealOverviewSection = React.memo(function MemoizedDealOverviewSection(props: any) {
  return <DealOverviewSection {...props} />
})

interface DrawerProps {
  deal?: any | null
  onSubmit?: (data: any) => Promise<void>
  loading?: boolean
  onRefreshInteractionsReady?: (refreshFn: () => Promise<void>) => void
  onRefreshTasksReady?: (refreshFn: () => Promise<void>) => void
}

export function Drawer({ deal = null, onRefreshInteractionsReady, onRefreshTasksReady }: DrawerProps) {
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()

  const dealId = deal?.[DealFields.deals__id] || deal?.id
  const fundOptions = useSelector((state: any) => state.globalConfigs.funds || [])

  const searchParams = new URLSearchParams(location.search)
  const hideInteractions = searchParams.get('hideInteractions') === 'true'
  const initialTaskId = searchParams.get('taskId')

  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectedItemType, setSelectedItemType] = useState<'task' | 'interaction' | null>(null)
  const [detailMode, setDetailMode] = useState<
    'view' | 'add-task' | 'add-interaction' | 'edit-task' | 'edit-interaction'
  >('view')
  const [, setIsTaskInEditMode] = useState<boolean>(false)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)

  const [formData, setFormData] = useState<Record<string, any>>({})
  const [commitments, setCommitments] = useState<any[]>([])

  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
    deal_team_lead: any[]
  }>({
    introduced_by: [],
    sourced_by: [],
    deal_team_lead: []
  })

  useEffect(() => {
    const isDetailPanelOpen =
      (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction'

    const isTaskOrInteractionEditing =
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (detailMode === 'add-task' ||
        detailMode === 'edit-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-interaction' ||
        selectedItem)

    if (isDetailPanelOpen) {
      setRightMenuWidth('55%')
    } else if (isTaskOrInteractionEditing) {
      setRightMenuWidth('52.5%')
    } else {
      resetRightMenuWidth()
    }

    return () => {
      resetRightMenuWidth()
    }
  }, [selectedItem, detailMode, selectedItemType, setRightMenuWidth, resetRightMenuWidth])

  useEffect(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
    setIsTaskInEditMode(false)
  }, [dealId])

  const dealFromActiveRecords = useSelector((state: any) => (dealId ? selectActiveRecordById(state, dealId) : null))
  const dealFromState = useSelector((state: any) => selectDealById(state, dealId))
  const currentDeal = dealFromActiveRecords || deal || dealFromState

  const previousDealIdRef = useRef<string | number | null>(null)

  useEffect(() => {
    const dealIdString = dealId?.toString()
    const previousDealIdString = previousDealIdRef.current?.toString()

    if (dealIdString && dealIdString !== previousDealIdString && deal) {
      dispatch(setActiveRecord({ uuid: dealIdString, record: deal }))
      dispatch(registerRecordUsage({ uuid: dealIdString, context: 'drawer' }))
      previousDealIdRef.current = dealId
    }

    return () => {
      if (previousDealIdString) {
        dispatch(unregisterRecordUsage({ uuid: previousDealIdString, context: 'drawer' }))
      }
    }
  }, [dealId, dispatch])

  const tasks = useMemo(() => {
    if (!currentDeal) return []
    return currentDeal[DealFields.prog__tasks] || []
  }, [currentDeal])

  useEffect(() => {
    if (initialTaskId && tasks && tasks.length > 0) {
      const targetTask = tasks.find(
        (task: any) => task[TaskRecordFields.tasks__id]?.toString() === initialTaskId.toString()
      )
      if (targetTask) {
        setSelectedItem(targetTask)
        setSelectedItemType('task')
        setDetailMode('view')
      }
    }
  }, [initialTaskId, tasks])

  useEffect(() => {
    if (currentDeal) {
      const initialData: Record<string, any> = {
        [DealFields.deals__name]: currentDeal[DealFields.deals__name],
        [DealFields.deals__currency]: currentDeal[DealFields.deals__currency],
        [DealFields.pipeline_deals__stage]: currentDeal[DealFields.pipeline_deals__stage],
        [DealFields.rounds__funding_stage]: currentDeal[DealFields.rounds__funding_stage],
        [DealFields.rounds__raise_amount]: currentDeal[DealFields.rounds__raise_amount],
        [DealFields.rounds__pre_money_valuation]:
          currentDeal[DealFields.rounds__pre_money_valuation] ||
          (currentDeal.rounds?.length > 0
            ? currentDeal.rounds
                .slice()
                .sort((a: any, b: any) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime())[0]
                ?.pre_money_valuation
            : null),
        [DealFields.rounds__post_money_valuation]:
          currentDeal[DealFields.rounds__post_money_valuation] ||
          (currentDeal.rounds?.length > 0
            ? currentDeal.rounds
                .slice()
                .sort((a: any, b: any) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime())[0]
                ?.post_money_valuation
            : null),
        [DealFields.deals__allocation_available]: currentDeal[DealFields.deals__allocation_available]
      }
      setFormData(initialData)

      const dealCommitmentsData = currentDeal[DealFields.prog__deal_commitments] || []
      if (dealCommitmentsData && dealCommitmentsData.length > 0) {
        const dealCommitments = dealCommitmentsData
          .filter((commitment: any) => {
            const amount = commitment[DealCommitmentEntryFields.deal_commitments__amount]
            return amount !== null && amount !== undefined && amount > 0
          })
          .map((commitment: any, index: number) => {
            const fundId = commitment[DealCommitmentEntryFields.deal_commitments__fund_id]
            const amount = commitment[DealCommitmentEntryFields.deal_commitments__amount]
            const commitmentId = commitment[DealCommitmentEntryFields.deal_commitments__id]
            const fundOption = fundOptions?.find((option: any) => option.id === fundId)
            return {
              key: commitmentId || `commitment_${index}`,
              fund: fundId,
              fundId: fundId,
              fundName: fundId ? fundOption?.name || `Fund ID: ${fundId}` : 'No Fund Selected',
              amount: amount || 0
            }
          })
        setCommitments(dealCommitments)
      } else {
        setCommitments([])
      }

      const contactsByRole: {
        introduced_by: any[]
        sourced_by: any[]
        deal_team_lead: any[]
      } = {
        introduced_by: [],
        sourced_by: [],
        deal_team_lead: []
      }

      const introducedByDetail =
        currentDeal[DealFields.contact_accounts__introduced_by] || currentDeal.introduced_by_detail
      if (introducedByDetail && Array.isArray(introducedByDetail)) {
        contactsByRole.introduced_by = introducedByDetail.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const sourcedByDetails = currentDeal[DealFields.contact_accounts__sourced_by] || currentDeal.sourced_by_details
      if (sourcedByDetails && Array.isArray(sourcedByDetails)) {
        contactsByRole.sourced_by = sourcedByDetails.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      const leadDetail = currentDeal[DealFields.contact_deals__lead] || currentDeal.lead_detail
      if (leadDetail && Array.isArray(leadDetail)) {
        contactsByRole.deal_team_lead = leadDetail.map((contact: any) => ({
          value: contact[UserEntryFields.contacts__id] || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || '',
          data: contact
        }))
      }

      setSelectedContacts(contactsByRole)
    }
  }, [currentDeal, fundOptions])

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handlePatchSuccess = useCallback(
    (patchRecordId: string, updates: Record<string, any>) => {
      setFormData((prev) => ({ ...prev, ...updates }))

      if (dealId) {
        dispatch(
          updateDealRecord({
            dealId: dealId.toString(),
            updates
          })
        )
      }
    },
    [dealId, dispatch]
  )

  const handleCommitmentChange = useCallback((updatedCommitments: any[]) => {
    setCommitments(updatedCommitments)
  }, [])

  const handleContactsChange = (updatedContacts: DealContacts) => {
    setSelectedContacts(updatedContacts)
  }

  const handleTaskSelect = useCallback((task: any) => {
    if (task === null) {
      setSelectedItem(null)
      setSelectedItemType(null)
      setDetailMode('view')
    } else {
      setSelectedItem(task)
      setSelectedItemType('task')
      setDetailMode('view')
    }
  }, [])

  const handleAddTask = useCallback(() => {
    setDetailMode('add-task')
    setSelectedItem(null)
    setSelectedItemType('task')
  }, [])

  const handleTaskEditModeChange = useCallback((isInEditMode: boolean) => {
    setIsTaskInEditMode(isInEditMode)
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
    setDetailMode('view')
  }, [])

  const handleAddInteraction = useCallback(() => {
    setDetailMode('add-interaction')
    setSelectedItem(null)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
    setDetailMode('view')
  }, [])

  const handleDetailEdit = useCallback((item: any, itemType: string) => {
    if (itemType === 'task') {
      setDetailMode('edit-task')
    } else if (itemType === 'interaction') {
      setDetailMode('edit-interaction')
    }
  }, [])

  const handleDetailDelete = useCallback(
    async (id: string) => {
      if (!dealId || !id) {
        throw new Error('Deal ID is missing')
      }

      if (selectedItemType === 'task') {
        try {
          await dispatch(deleteTaskThunk({ dealId, taskId: id, recordType: 'deal' })).unwrap()
          setTimeout(() => {
            handleDetailClose()
          }, 100)
        } catch (error) {
          console.error('Error in handleDetailDelete:', error)
          setTimeout(() => {
            handleDetailClose()
          }, 100)
          throw error
        }
        return
      }

      const interactionId = id
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction(dealId, interactionId, currentToken)
        dispatch(removeInteraction(dealId, interactionId))
        const refreshedInteractions = await fetchInteractionsForDeal(dealId, {}, currentToken)
        dispatch(setInteractions(dealId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelete:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [dealId, dispatch, handleDetailClose, selectedItemType]
  )

  const handleDetailDelink = useCallback(
    async (interactionId: string) => {
      if (!dealId || !interactionId) {
        throw new Error('Deal ID or Interaction ID is missing')
      }

      try {
        const currentToken = getAccessToken(store.getState().auth)
        await delinkInteraction(dealId, interactionId, 'deal', currentToken)
        dispatch(removeInteraction(dealId, interactionId))
        const refreshedInteractions = await fetchInteractionsForDeal(dealId, {}, currentToken)
        dispatch(setInteractions(dealId, refreshedInteractions))
        setTimeout(() => {
          handleDetailClose()
        }, 100)
      } catch (error) {
        console.error('Error in handleDetailDelink:', error)
        setTimeout(() => {
          handleDetailClose()
        }, 100)
        throw error
      }
    },
    [dealId, dispatch, handleDetailClose]
  )

  const handleRefreshTasks = useCallback(async () => {
    if (refreshTasksRef.current) {
      await refreshTasksRef.current()
    }
  }, [])

  const handleDetailSubmit = async (data: any, mode: string) => {
    if (!dealId || dealId === 'new') {
      console.warn('Cannot save task/interaction: Deal ID is not available')
      return
    }

    try {
      const currentToken = getAccessToken(store.getState().auth)
      if (mode === 'add-task') {
        const taskData = {
          ...data,
          [TaskRecordFields.task_deals__deal_id]: dealId
        }
        const accountIdValue =
          currentDeal?.[DealFields.deals__account] ||
          currentDeal?.[DealFields.accounts__id] ||
          null
        const accountId =
          typeof accountIdValue === 'object' && accountIdValue?.id
            ? accountIdValue.id
            : accountIdValue
        const createdTask = await createTask(
          dealId,
          taskData,
          currentToken,
          'deal',
          accountId
        )

        if (currentDeal) {
          const existingTasks = currentDeal[DealFields.prog__tasks] || []
          const updatedTasks = [createdTask, ...existingTasks]
          dispatch(
            updateDealRecord({
              dealId: dealId,
              updates: {
                [DealFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'edit-task') {
        const taskData = {
          ...data
        }
        const taskId = selectedItem?.[TaskRecordFields.tasks__id]
        const updatedTask = await updateTask(dealId, taskId, taskData, currentToken)

        if (currentDeal) {
          const existingTasks = currentDeal[DealFields.prog__tasks] || []
          const updatedTasks = existingTasks.map((t: any) =>
            t[TaskRecordFields.tasks__id] === taskId ? updatedTask : t
          )
          dispatch(
            updateDealRecord({
              dealId: dealId,
              updates: {
                [DealFields.prog__tasks]: updatedTasks
              }
            })
          )
        }
        await handleRefreshTasks()
      } else if (mode === 'add-interaction') {
        const interactionData = {
          ...data,
          [InteractionRecordFields.interaction_deals__deal_id]: dealId
        }
        const accountIdRaw =
          currentDeal?.[DealFields.deals__account] ||
          currentDeal?.[DealFields.accounts__id] ||
          null
        const resolvedAccountId =
          typeof accountIdRaw === 'object' && accountIdRaw?.id ? accountIdRaw.id : accountIdRaw
        const createdInteraction = await createInteraction(
          dealId,
          interactionData,
          currentToken,
          'deal',
          resolvedAccountId
        )

        dispatch(addInteraction(dealId, createdInteraction))
      } else if (mode === 'edit-interaction') {
        const interactionData = {
          ...data
        }
        const interactionId = selectedItem?.[InteractionRecordFields.interactions__id]
        const updatedInteraction = await updateInteraction(dealId, interactionId, interactionData, currentToken)

        dispatch(updateInteractionAction(dealId, interactionId, updatedInteraction))
      }

      setDetailMode('view')
    } catch (error) {
      console.error('Failed to save:', error)
      throw error
    }
  }

  const isFullWidthMode = useMemo(() => {
    return (
      (selectedItemType === 'task' || selectedItemType === 'interaction') &&
      (selectedItem ||
        detailMode === 'add-task' ||
        detailMode === 'add-interaction' ||
        detailMode === 'edit-task' ||
        detailMode === 'edit-interaction')
    )
  }, [selectedItemType, selectedItem, detailMode])

  const isDetailPanelOpen = useMemo(() => {
    return (selectedItem || detailMode !== 'view') && selectedItemType !== 'task' && selectedItemType !== 'interaction'
  }, [selectedItem, detailMode, selectedItemType])

  const tasksSectionProps = useMemo(
    () => ({
      dealId,
      onTaskSelect: handleTaskSelect,
      selectedTaskId: selectedItemType === 'task' ? selectedItem?.[TaskRecordFields.tasks__id] : null,
      onAddTask: handleAddTask,
      onEditModeChange: handleTaskEditModeChange,
      disableInlineEditing: true,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshTasksRef.current = refreshFn
        if (onRefreshTasksReady) {
          onRefreshTasksReady(refreshFn)
        }
      }
    }),
    // eslint-disable-next-line
    [
      dealId,
      selectedItemType,
      selectedItem?.[TaskRecordFields.tasks__id],
      handleTaskSelect,
      handleAddTask,
      handleTaskEditModeChange,
      onRefreshTasksReady
    ]
  )

  const interactionsSectionProps = useMemo(
    () => ({
      dealId,
      onInteractionSelect: handleInteractionSelect,
      selectedInteractionId:
        selectedItemType === 'interaction' ? selectedItem?.[InteractionRecordFields.interactions__id] : null,
      onAddInteraction: handleAddInteraction,
      onRefreshRequest: (refreshFn: () => Promise<void>) => {
        refreshInteractionsRef.current = refreshFn
        if (onRefreshInteractionsReady) {
          onRefreshInteractionsReady(refreshFn)
        }
      },
      disableInlineEditing: true
    }),
    // eslint-disable-next-line
    [
      dealId,
      selectedItemType,
      selectedItem?.[InteractionRecordFields.interactions__id],
      handleInteractionSelect,
      handleAddInteraction,
      onRefreshInteractionsReady
    ]
  )

  const dealOverviewSectionProps = useMemo(
    () => ({
      deal: currentDeal,
      formData,
      handleFieldChange,
      onPatchSuccess: handlePatchSuccess,
      commitments,
      handleCommitmentChange,
      selectedContacts,
      onContactsChange: handleContactsChange,
      isDrawer: true,
      showCurrency: false,
      showTitle: true,
      cardProps: { className: 'mb-4' },
      recordId: dealId
    }),
    [
      currentDeal,
      formData,
      handlePatchSuccess,
      commitments,
      handleCommitmentChange,
      selectedContacts,
      dealId
    ]
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='drawer-existing-container'
        data-component='DrawerExisting'
        data-deal-id={dealId}
        data-selected-item-type={selectedItemType}
        data-detail-mode={detailMode}
        data-hide-interactions={hideInteractions}
        className='h-full'
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <div data-debug='drawer-unified-layout' className='flex flex-col h-full'>
          <div data-debug='drawer-overview-compact' className='flex-shrink-0'>
            <MemoizedDealOverviewSection {...dealOverviewSectionProps} />
          </div>

          <Row data-debug='drawer-content-row' gutter={[16, 16]} className='h-full flex-1 min-h-0'>
            <Col span={isFullWidthMode ? 10 : isDetailPanelOpen ? 10 : 24} className='h-full flex flex-col min-h-0'>
              <div data-debug='drawer-tasks-section' className='mb-1 flex-shrink-0'>
                <MemoizedTasksSection {...tasksSectionProps} />
              </div>

              {!hideInteractions && (
                <div data-debug='drawer-interactions-section' className='flex flex-col flex-1 min-h-0'>
                  <div className='h-full min-h-[280px]'>
                    <MemoizedInteractionsSection {...interactionsSectionProps} />
                  </div>
                </div>
              )}
            </Col>

            {(isFullWidthMode || isDetailPanelOpen) && (
              <Col span={14} className='h-full flex flex-col min-h-0'>
                <DetailPanel
                  selectedItem={selectedItem}
                  itemType={selectedItemType || undefined}
                  mode={detailMode}
                  onClose={handleDetailClose}
                  onEdit={handleDetailEdit}
                  onSubmit={handleDetailSubmit}
                  onDelete={handleDetailDelete}
                  onDelink={handleDetailDelink}
                  onRefreshInteractions={async () => {
                    if (refreshInteractionsRef.current) {
                      await refreshInteractionsRef.current()
                    }
                  }}
                  onRefreshTasks={handleRefreshTasks}
                />
              </Col>
            )}
          </Row>
        </div>
      </div>
    </DndProvider>
  )
}
